import React, { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Store, Package, ShoppingCart, Loader2 } from 'lucide-react';
import { useMarketplace } from '@/hooks/useMarketplace';
import ModuleCard from './ModuleCard';
import ModuleSubscriptionDialog from './ModuleSubscriptionDialog';
import ModuleDeactivationDialog from './ModuleDeactivationDialog';
import { ModuleWithStatus } from '@/types/marketplace';

const MarketplaceView = () => {
  const {
    modulesWithStatus,
    myLicense,
    isLoading,
    isRequesting,
    isRemoving,
    isReactivating,
    requestModule,
    removeModule,
    reactivateModule,
    activeModulesCount,
    availableModulesCount,
  } = useMarketplace();

  const [selectedModule, setSelectedModule] = useState<ModuleWithStatus | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [deactivateModule, setDeactivateModule] = useState<ModuleWithStatus | null>(null);

  const activeModules = modulesWithStatus.filter(m => m.status === 'active' || m.status === 'pending_cancellation');
  const availableModules = modulesWithStatus.filter(m => m.status === 'available');

  const handleSubscribe = (module: ModuleWithStatus) => {
    setSelectedModule(module);
    setDialogOpen(true);
  };

  const handleConfirmSubscription = async () => {
    if (selectedModule) {
      await requestModule(selectedModule.code);
    }
  };

  const handleDeactivate = (module: ModuleWithStatus) => {
    setDeactivateModule(module);
    setDeactivateDialogOpen(true);
  };

  const handleConfirmDeactivation = async () => {
    if (deactivateModule) {
      await removeModule(deactivateModule.code);
    }
  };

  const handleReactivate = async (module: ModuleWithStatus) => {
    await reactivateModule(module.code);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const isBusy = isRequesting || isRemoving || isReactivating;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 bg-primary/10 rounded-lg">
          <Store className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Marketplace de Módulos</h1>
          <p className="text-muted-foreground">
            Expanda as funcionalidades da sua conta
          </p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="all" className="space-y-4">
        <TabsList>
          <TabsTrigger value="my-modules" className="gap-2">
            <Package className="h-4 w-4" />
            Meus Módulos ({activeModulesCount})
          </TabsTrigger>
          <TabsTrigger value="available" className="gap-2">
            <ShoppingCart className="h-4 w-4" />
            Disponíveis ({availableModulesCount})
          </TabsTrigger>
          <TabsTrigger value="all">
            Todos
          </TabsTrigger>
        </TabsList>

        <TabsContent value="my-modules" className="space-y-4">
          {activeModules.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Você ainda não possui módulos ativos.</p>
              <p className="text-sm">Explore os módulos disponíveis e adicione à sua conta.</p>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {activeModules.map((module) => (
                <ModuleCard
                  key={module.id}
                  module={module}
                  onSubscribe={handleSubscribe}
                  onDeactivate={handleDeactivate}
                  onReactivate={handleReactivate}
                  isRequesting={isBusy}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="available" className="space-y-4">
          {availableModules.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <ShoppingCart className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Você já possui todos os módulos disponíveis!</p>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {availableModules.map((module) => (
                <ModuleCard
                  key={module.id}
                  module={module}
                  onSubscribe={handleSubscribe}
                  isRequesting={isRequesting}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="all" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {modulesWithStatus.map((module) => (
              <ModuleCard
                key={module.id}
                module={module}
                onSubscribe={handleSubscribe}
                onDeactivate={handleDeactivate}
                onReactivate={handleReactivate}
                isRequesting={isBusy}
              />
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Subscription Dialog */}
      <ModuleSubscriptionDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        module={selectedModule}
        license={myLicense}
        onConfirm={handleConfirmSubscription}
        isLoading={isRequesting}
      />

      {/* Deactivation Dialog */}
      <ModuleDeactivationDialog
        open={deactivateDialogOpen}
        onOpenChange={setDeactivateDialogOpen}
        module={deactivateModule}
        onConfirm={handleConfirmDeactivation}
        isLoading={isRemoving}
      />
    </div>
  );
};

export default MarketplaceView;
